import React from 'react'
import PropTypes, { any } from 'prop-types'
import { useSelector } from 'react-redux'
import { DataGrid } from '@mui/x-data-grid'
import moment from 'moment'
import { useTheme } from '../../../../../components/mui/Styles/index'
import StatusChip from '../../../../../components/genericcomponents/StatusChip/status-chip'
import LinkButton from '../../../../../components/LinkButton/link-button'
import { getCurrencyFormattedValue } from '../../../../../utils/string-utils'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'
import Constants from '../../../../../constants/en.json'

export function InvoiceTable({ filterBy, filterValue, handleDisplayInvoice, searchByOptions }) {
  const theme = useTheme()
  const invoices = useSelector((state) => state.invoice.invoices)
  const isFetchingInvoices = useSelector((state) => state.invoice.isFetchingInvoices)
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''
  const filterByType = selectedSearchOption[0]?.type
  const noResultsValue = filterByType === 'date' ? moment.utc(filterValue).format('L') : filterValue
  const handleInvoicePdfLinkClick = async (row) => {
    handleDisplayInvoice(row.id)
  }

  const rows = invoices.map((row) => ({
    id: row.invoiceId,
    invoiceNumber: row.invoiceNumber,
    invoiceDateIssued: row.issuedDate,
    invoiceDueDate: row.dueDate,
    invoiceType: row.invoiceType,
    invoiceAmount: row.invoiceAmount,
    paymentStatus: row.paymentStatus,
    invoiceAttachment: row.invoiceAttachment,
  }))

  const columns = [
    {
      field: 'invoiceNumber',
      headerName: Constants.creditInvoice.INVOICE_NUMBER,
      flex: 0.08,
      minWidth: 120,
    },
    {
      field: 'invoiceDateIssued',
      headerName: Constants.creditInvoice.DATE_ISSUED,
      headerAlign: 'right',
      align: 'right',
      valueGetter: (value) => value,
      valueFormatter: (value) => value && moment.utc(value).format('L'),
      flex: 0.08,
      minWidth: 120,
    },
    {
      field: 'invoiceDueDate',
      headerName: Constants.creditInvoice.DATE_DUE,
      headerAlign: 'right',
      align: 'right',
      valueGetter: (value) => value,
      valueFormatter: (value) => value && moment.utc(value).format('L'),
      flex: 0.08,
      minWidth: 120,
    },
    {
      field: 'invoiceType',
      headerName: Constants.creditInvoice.INVOICE_TYPE,
      flex: 0.08,
      minWidth: 120,
    },
    {
      field: 'invoiceAmount',
      headerName: Constants.creditInvoice.AMOUNT,
      minWidth: 100,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => {
        return getCurrencyFormattedValue(value)
      },
      sortComparator: (a, b) => Number(a) - Number(b),
      flex: 0.08,
    },
    {
      field: 'paymentStatus',
      headerName: Constants.creditInvoice.STATUS,
      minWidth: 150,
      renderCell: (cellValues) => {
        return <StatusChip status={cellValues.row.paymentStatus} />
      },
      flex: 0.08,
    },
    {
      field: 'invoiceAttachment',
      headerName: Constants.creditInvoice.ATTACHMENT,
      minWidth: 150,
      renderCell: (cellValues) => {
        const invoiceAttachment = cellValues.row.invoiceAttachment
        return (
          <LinkButton
            handleLinkClick={() => {
              handleInvoicePdfLinkClick(cellValues.row)
            }}
            linkText={invoiceAttachment}
          />
        )
      },
      flex: 0.09,
    },
  ]

  const gridStyle = {
    minHeight: '160px',
    height: isFetchingInvoices ? '400px' : 'auto',
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
  }

  const filterOperator = filterBy === 'paymentStatus' ? 'equals' : 'contains'

  const filterModel = {
    items: [{ field: filterBy, operator: filterOperator, value: filterValue }],
  }

  return (
    <DataGrid
      sx={gridStyle}
      rows={rows}
      columns={columns}
      loading={isFetchingInvoices}
      spacing={1}
      pageSizeOptions={[25, 50]}
      filterModel={filterModel}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 25, page: 0 },
        },
      }}
      slotProps={{
        pagination: {
          component: 'div',
          showLastButton: true,
          showFirstButton: true,
        },
        noResultsOverlay: {
          filterByDescription,
          noResultsValue,
        },
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
      slots={{
        noResultsOverlay: NoResultsFoundTableOverlay,
      }}
      disableColumnFilter
      disableRowSelectionOnClick
    />
  )
}

InvoiceTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  handleDisplayInvoice: PropTypes.func.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
}

export default InvoiceTable
